import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSpinner,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaCalculator,
  FaUser,
  FaArrowRight,
  FaTrophy,
  FaGraduationCap,
  FaSearch,
  FaComments,
  FaChartLine,
  FaHistory,
  FaBook,
  FaFolderOpen,
  FaThLarge,
  FaBell,
  FaCog,
  FaMoon,
  FaSun,
  FaRobot,
  FaBrain,
  FaChartBar,
  FaRocket,
  FaShieldAlt,
  FaCloudUploadAlt,
  FaFileAlt,
  FaUserGraduate,
  FaAward,
  FaStar,
  FaFire,
  FaCalendarAlt,
  FaFilePdf,
} from "react-icons/fa";
import {
  FiUploadCloud,
  FiDownload,
  FiHeart,
  FiEye,
  FiBookOpen,
  FiTrendingUp,
  FiBarChart2,
  FiGrid,
  FiList,
  FiActivity,
  FiTarget,
  FiFolder,
} from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import ResultComparison from "../components/results/ResultComparison";
import { useFooter } from "../context/FooterContext";

// ========== ADDED: Announcement Component Imports ==========
import NotificationBell from "../components/common/NotificationBell";
import AnnouncementPopup from "../components/common/AnnouncementPopup";
import AnnouncementBar from "../components/common/AnnouncementBar";
// ========== END OF ADDED IMPORTS ==========

const Dashboard = () => {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [stats, setStats] = useState({
    totalUploads: 0,
    totalDownloads: 0,
    totalLikes: 0,
    totalViews: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
  });
  const [recentNotes, setRecentNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const [showRecentResults, setShowRecentResults] = useState(false);
  const [recentResultData, setRecentResultData] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [overallCGPA, setOverallCGPA] = useState(null);
  const [sgpaHistory, setSgpaHistory] = useState([]);
  const [cgpaLoading, setCgpaLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const [backlogCount, setBacklogCount] = useState(0);
  const [hasBacklogs, setHasBacklogs] = useState(false);
  const [showBacklogDetails, setShowBacklogDetails] = useState(false);
  const [backlogSubjects, setBacklogSubjects] = useState([]);

  const [activeTab, setActiveTab] = useState("overview");
  const [viewMode, setViewMode] = useState("grid");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 18) setGreeting("Good afternoon");
    else setGreeting("Good evening");
  }, []);

  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark !== false);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate("/login");
      return;
    }
    fetchDashboardData();
    fetchStudentCGPA();
  }, [user, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchBacklogs = async () => {
      const userUSN = user?.usn;
      if (!userUSN) return;

      try {
        const response = await axios.get(`/api/academic/backlogs/${userUSN}`);
        setBacklogCount(response.data.totalBacklogs);
        setHasBacklogs(response.data.hasBacklogs);
        setBacklogSubjects(response.data.backlogSubjects);
      } catch (error) {
        console.error("Fetch backlogs error:", error);
      }
    };

    if (user?.usn) {
      fetchBacklogs();
    }
  }, [user]);

  const fetchStudentCGPA = async () => {
    if (!user?._id && !user?.id) return;

    setCgpaLoading(true);
    try {
      const userId = user._id || user.id;
      const token = localStorage.getItem("token");

      const response = await axios
        .get(`/api/academic/student/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => ({ data: null }));

      if (response.data) {
        setOverallCGPA(response.data.cgpa);
        setSgpaHistory(response.data.sgpaHistory || []);
      }
    } catch (error) {
      console.error("Failed to fetch CGPA data:", error);
      setOverallCGPA(null);
      setSgpaHistory([]);
    } finally {
      setCgpaLoading(false);
    }
  };

  const refreshAcademicData = () => {
    fetchStudentCGPA();
  };

  const fetchDashboardData = async () => {
    if (!user?._id && !user?.id) {
      console.error("User ID not found");
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const userId = user._id || user.id;

      const notesResponse = await axios.get(`/api/notes/user/${userId}`);
      const userNotes = notesResponse.data || [];
      setRecentNotes(userNotes.slice(0, 5));

      const totalViews = userNotes.reduce(
        (sum, note) => sum + (note.views || 0),
        0,
      );
      const totalDownloads = userNotes.reduce(
        (sum, note) => sum + (note.downloads || 0),
        0,
      );
      const totalLikes = userNotes.reduce(
        (sum, note) => sum + (note.likes?.length || 0),
        0,
      );
      const pendingCount = userNotes.filter(
        (n) => n.status === "pending",
      ).length;
      const approvedCount = userNotes.filter(
        (n) => n.status === "approved",
      ).length;
      const rejectedCount = userNotes.filter(
        (n) => n.status === "rejected",
      ).length;

      setStats({
        totalUploads: userNotes.length,
        totalDownloads,
        totalLikes,
        totalViews,
        pendingCount,
        approvedCount,
        rejectedCount,
      });
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return {
          icon: <FaClock size={12} />,
          text: "Pending Approval",
          style: {
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "white",
          },
        };
      case "approved":
        return {
          icon: <FaCheckCircle size={12} />,
          text: "Approved",
          style: {
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "white",
          },
        };
      case "rejected":
        return {
          icon: <FaTimesCircle size={12} />,
          text: "Rejected",
          style: {
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            color: "white",
          },
        };
      default:
        return {
          icon: null,
          text: "Unknown",
          style: {
            background: "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
            color: "white",
          },
        };
    }
  };

  const getGradeFromCGPA = (cgpa) => {
    if (!cgpa)
      return { grade: "Not Calculated", color: "#6c757d", icon: <FaBrain /> };
    if (cgpa >= 9.0)
      return { grade: "Outstanding", color: "#f59e0b", icon: <FaAward /> };
    if (cgpa >= 8.0)
      return { grade: "Excellent", color: "#10b981", icon: <FaStar /> };
    if (cgpa >= 7.0)
      return { grade: "Very Good", color: "#3b82f6", icon: <FaChartBar /> };
    if (cgpa >= 6.0)
      return { grade: "Good", color: "#8b5cf6", icon: <FaRocket /> };
    if (cgpa >= 5.0)
      return { grade: "Average", color: "#f59e0b", icon: <FiTarget /> };
    return { grade: "Needs Improvement", color: "#ef4444", icon: <FaFire /> };
  };

  const gradeInfo = overallCGPA
    ? getGradeFromCGPA(overallCGPA)
    : { grade: "Not Calculated", color: "#6c757d", icon: <FaBrain /> };

  const handleResultFetched = (result) => {
    setRecentResultData(result);
    toast.success("Result fetched successfully!");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 400, damping: 25 },
    },
  };

  const statCardVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 500, damping: 30 },
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: { type: "spring", stiffness: 400, damping: 17 },
    },
  };

  if (loading) return <Loader />;

  if (!user) {
    return (
      <div style={{ textAlign: "center", padding: "4rem" }}>
        <h2>Please login to view dashboard</h2>
        <Link to="/login" style={{ color: "#8b5cf6" }}>
          Go to Login
        </Link>
      </div>
    );
  }

  const containerPadding = isMobile ? "1rem" : "2rem";
  const cardPadding = isMobile ? "1rem" : "1.5rem";

  // Button style configurations
  const buttonStyles = {
    primary: {
      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
      boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
    },
    success: {
      background: "linear-gradient(135deg, #10b981, #059669)",
      boxShadow: "0 4px 14px 0 rgba(16,185,129,0.4)",
    },
    warning: {
      background: "linear-gradient(135deg, #f59e0b, #d97706)",
      boxShadow: "0 4px 14px 0 rgba(245,158,11,0.4)",
    },
    danger: {
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      boxShadow: "0 4px 14px 0 rgba(239,68,68,0.4)",
    },
    info: {
      background: "linear-gradient(135deg, #06b6d4, #0891b2)",
      boxShadow: "0 4px 14px 0 rgba(6,182,212,0.4)",
    },
    secondary: {
      background: "linear-gradient(135deg, #6b7280, #4b5563)",
      boxShadow: "0 4px 14px 0 rgba(107,114,128,0.4)",
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      style={{
        maxWidth: "1440px",
        margin: "0 auto",
        padding: containerPadding,
        minHeight: "100vh",
        background: isDarkMode ? "#0f0f1a" : "#f8fafc",
      }}
    >
      {/* Animated Background Particles */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 10,
            }}
            style={{
              position: "absolute",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              background: `linear-gradient(135deg, #8b5cf6, #3b82f6)`,
              borderRadius: "50%",
              filter: "blur(2px)",
            }}
          />
        ))}
      </div>

      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
        style={{
          background: isDarkMode
            ? "rgba(30, 30, 45, 0.8)"
            : "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(20px)",
          borderRadius: "28px",
          marginBottom: "2rem",
          position: "relative",
          overflow: "hidden",
          border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        }}
      >
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
          }}
        />

        <div style={{ padding: "2rem", position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              flexWrap: "wrap",
              gap: "1.5rem",
            }}
          >
            <div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.8rem",
                  marginBottom: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  <div
                    style={{
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      padding: "0.5rem",
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <HiOutlineSparkles size={24} color="white" />
                  </div>
                </motion.div>
                <div>
                  <p
                    style={{
                      color: isDarkMode ? "#a0aec0" : "#64748b",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    {greeting}
                  </p>
                  <h1
                    style={{
                      fontSize: isMobile ? "1.75rem" : "2.25rem",
                      marginBottom: "0",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontWeight: 700,
                    }}
                  >
                    {user?.name?.split(" ")[0] || "User"}
                  </h1>
                </div>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{
                  color: isDarkMode ? "#cbd5e1" : "#475569",
                  fontSize: isMobile ? "0.875rem" : "0.95rem",
                  marginLeft: "3rem",
                }}
              >
                Welcome to your Academic Dashboard
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              style={{ display: "flex", gap: "0.75rem" }}
            >
              {/* ========== ADDED: NotificationBell component for announcements ========== */}
              <NotificationBell isDarkMode={isDarkMode} />
              {/* ========== END OF ADDED ========== */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/profile"
                  style={{
                    padding: "0.6rem 1.25rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    transition: "all 0.3s",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    boxShadow: "0 2px 8px rgba(139,92,246,0.3)",
                  }}
                >
                  <FaUser size={14} /> View Profile
                  <FaArrowRight size={10} />
                </Link>
              </motion.div>
            </motion.div>
          </div>

          {/* Stats Badges Row - REMOVED Pending and Approved badges */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            style={{
              display: "flex",
              gap: "0.75rem",
              marginTop: "1.75rem",
              flexWrap: "wrap",
            }}
          >
            {/* Backlog Badge Only - No Pending/Approved badges */}
            <motion.div
              whileHover={{
                scale: hasBacklogs ? 1.05 : 1,
                y: hasBacklogs ? -2 : 0,
              }}
              onClick={() => hasBacklogs && setShowBacklogDetails(true)}
              style={{
                background: hasBacklogs
                  ? "rgba(239, 68, 68, 0.15)"
                  : "rgba(16, 185, 129, 0.15)",
                padding: "0.4rem 1rem",
                borderRadius: "40px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: hasBacklogs ? "pointer" : "default",
                fontSize: "0.8rem",
                border: `1px solid ${hasBacklogs ? "rgba(239, 68, 68, 0.3)" : "rgba(16, 185, 129, 0.3)"}`,
              }}
            >
              <FiBookOpen
                size={12}
                color={hasBacklogs ? "#ef4444" : "#10b981"}
              />
              <span style={{ color: hasBacklogs ? "#ef4444" : "#10b981" }}>
                {hasBacklogs
                  ? `${backlogCount} Backlog${backlogCount !== 1 ? "s" : ""}`
                  : "No Backlogs"}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* ========== ADDED: Announcement Popup and Bar Components ========== */}
      <AnnouncementPopup isDarkMode={isDarkMode} />
      <AnnouncementBar isDarkMode={isDarkMode} />
      {/* ========== END OF ADDED ========== */}

      {/* Modern Tab Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        style={{
          display: "flex",
          gap: "0.5rem",
          marginBottom: "2rem",
          background: isDarkMode
            ? "rgba(30, 30, 45, 0.6)"
            : "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
          padding: "0.5rem",
          borderRadius: "20px",
          border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
        }}
      >
        {[
          { id: "overview", label: "Overview", icon: <FaThLarge size={14} /> },
          {
            id: "academics",
            label: "Academics",
            icon: <FaUserGraduate size={14} />,
          },
          { id: "notes", label: "My Notes", icon: <FaFolderOpen size={14} /> },
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: "0.75rem 1.25rem",
              background:
                activeTab === tab.id
                  ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                  : "transparent",
              border: "none",
              borderRadius: "16px",
              color:
                activeTab === tab.id
                  ? "white"
                  : isDarkMode
                    ? "#94a3b8"
                    : "#64748b",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              fontWeight: 600,
              fontSize: "0.875rem",
              transition: "all 0.3s",
            }}
          >
            {tab.icon}
            {!isMobile && tab.label}
          </motion.button>
        ))}
      </motion.div>

      <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4, type: "spring", stiffness: 300 }}
          >
            {/* Hero Stats Cards */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: "1.5rem",
                marginBottom: "2rem",
              }}
            >
              {/* CGPA Card */}
              <motion.div
                variants={statCardVariants}
                whileHover="hover"
                style={{
                  background:
                    "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
                  borderRadius: "28px",
                  padding: cardPadding,
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.1, 0.3],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                  style={{
                    position: "absolute",
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    background:
                      "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
                    borderRadius: "50%",
                  }}
                />
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1.5rem",
                      flexWrap: "wrap",
                      gap: "1rem",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          color: "#a78bfa",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          marginBottom: "0.25rem",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Overall CGPA
                      </p>
                      <h2
                        style={{
                          fontSize: isMobile ? "2.5rem" : "3rem",
                          fontWeight: "bold",
                          color: "white",
                          marginBottom: "0.25rem",
                          background: "linear-gradient(135deg, #fff, #c4b5fd)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {cgpaLoading ? (
                          <FaSpinner
                            style={{
                              animation: "spin 1s linear infinite",
                              color: "#8b5cf6",
                            }}
                          />
                        ) : (
                          overallCGPA || "—"
                        )}
                      </h2>
                      <p
                        style={{
                          color: "#a78bfa",
                          fontSize: isMobile ? "0.7rem" : "0.75rem",
                          fontWeight: "500",
                        }}
                      >
                        out of 10.0
                      </p>
                    </div>
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      style={{
                        width: isMobile ? "70px" : "80px",
                        height: isMobile ? "70px" : "80px",
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${gradeInfo.color}, ${gradeInfo.color}80)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 0 30px ${gradeInfo.color}40`,
                      }}
                    >
                      <div
                        style={{ fontSize: isMobile ? 28 : 32, color: "white" }}
                      >
                        {gradeInfo.icon}
                      </div>
                    </motion.div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "20px",
                      padding: "0.875rem",
                      marginTop: "0.5rem",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <p
                      style={{
                        color: "#c4b5fd",
                        fontSize: isMobile ? "0.7rem" : "0.75rem",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Performance Grade
                    </p>
                    <p
                      style={{
                        color: gradeInfo.color,
                        fontSize: isMobile ? "1rem" : "1.125rem",
                        fontWeight: "bold",
                      }}
                    >
                      {gradeInfo.grade}
                    </p>
                  </motion.div>
                </div>
              </motion.div>

              {/* SGPA History Card */}
              <motion.div
                variants={statCardVariants}
                whileHover="hover"
                style={{
                  background: isDarkMode
                    ? "rgba(30, 30, 45, 0.8)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "28px",
                  padding: cardPadding,
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  <div
                    style={{
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      padding: "0.5rem",
                      borderRadius: "14px",
                    }}
                  >
                    <FaChartLine color="white" size={18} />
                  </div>
                  <h3
                    style={{
                      fontSize: isMobile ? "1rem" : "1.125rem",
                      fontWeight: "bold",
                      color: isDarkMode ? "#f1f5f9" : "#1e293b",
                      margin: 0,
                    }}
                  >
                    Semester Performance
                  </h3>
                </div>
                {sgpaHistory.length > 0 ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "repeat(2, 1fr)"
                        : "repeat(4, 1fr)",
                      gap: "0.75rem",
                    }}
                  >
                    {sgpaHistory.map((sem, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.05 }}
                        whileHover={{ y: -3 }}
                        style={{
                          textAlign: "center",
                          padding: "0.75rem",
                          background: isDarkMode
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.02)",
                          borderRadius: "16px",
                          border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                        }}
                      >
                        <p
                          style={{
                            fontSize: isMobile ? "0.65rem" : "0.7rem",
                            color: isDarkMode ? "#94a3b8" : "#64748b",
                            marginBottom: "0.3rem",
                            fontWeight: 500,
                          }}
                        >
                          Semester {sem.semester}
                        </p>
                        <p
                          style={{
                            fontSize: isMobile ? "1rem" : "1.125rem",
                            fontWeight: "bold",
                            background:
                              "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {sem.sgpa}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "1.5rem" }}>
                    <p
                      style={{
                        color: isDarkMode ? "#94a3b8" : "#64748b",
                        fontSize: isMobile ? "0.7rem" : "0.8rem",
                      }}
                    >
                      No SGPA data available yet
                    </p>
                  </div>
                )}
                <motion.div whileHover={{ x: 5 }} style={{ marginTop: "1rem" }}>
                  <Link
                    to="/sgpa-calculator"
                    style={{
                      color: "#8b5cf6",
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      fontWeight: 500,
                    }}
                  >
                    Calculate SGPA <FaArrowRight size={10} />
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Quick Actions - Modern Grid with SaaS Buttons */}
            <motion.div
              variants={itemVariants}
              style={{ marginBottom: "2rem" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.25rem",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <h2
                  style={{
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: isMobile ? "1rem" : "1.125rem",
                    fontWeight: 600,
                  }}
                >
                  <FaRocket size={isMobile ? 18 : 20} color="#8b5cf6" />
                  Quick Actions
                </h2>
              </div>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/upload"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.primary.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.primary.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaCloudUploadAlt size={16} /> Upload Notes
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/cgpa-calculator"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.primary.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.primary.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaCalculator size={16} /> CGPA Calculator
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/sgpa-calculator"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.info.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.info.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaCalculator size={16} /> SGPA Calculator
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/results"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.warning.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.warning.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaSearch size={14} /> Check Results
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/chat"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.danger.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.danger.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaComments size={14} /> Community
                  </Link>
                </motion.div>
              </div>
            </motion.div>

            {/* Exam Resources Section with SaaS Buttons */}
            <motion.div
              variants={itemVariants}
              style={{ marginBottom: "2rem" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.25rem",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <h2
                  style={{
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    fontSize: isMobile ? "1rem" : "1.125rem",
                    fontWeight: 600,
                  }}
                >
                  <FaSearch color="#f59e0b" size={isMobile ? 16 : 18} />
                  Exam Resources
                </h2>
              </div>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/recent-results"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.warning.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.warning.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaHistory size={14} /> Recent Results
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/compare-results"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.info.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.info.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaChartLine size={14} /> Compare Results
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/pyqs"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.success.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.success.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaFilePdf size={14} /> Previous Year Papers
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/mqps"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.primary.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.primary.boxShadow,
                      transition: "all 0.3s",
                    }}
                  >
                    <FaFilePdf size={14} /> Model Question Papers
                  </Link>
                </motion.div>
                {/* ADDED: Learning Roadmap Button */}
                <motion.div
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/learning/my-learning"
                    style={{
                      padding: "0.75rem 1.5rem",
                      background: buttonStyles.primary.background,
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      boxShadow: buttonStyles.primary.boxShadow,
                    }}
                  >
                    <FaGraduationCap size={14} /> Learning Roadmap
                  </Link>
                </motion.div>
              </div>
            </motion.div>

            <AnimatePresence>
              {showComparison && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  style={{ marginBottom: "2rem" }}
                >
                  <ResultComparison />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {activeTab === "academics" && (
          <motion.div
            key="academics"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4 }}
          >
            {/* Academic Stats Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
                gap: "1.25rem",
                marginBottom: "2rem",
              }}
            >
              {[
                {
                  label: "Total Uploads",
                  value: stats.totalUploads,
                  icon: <FaCloudUploadAlt />,
                  gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                },
                {
                  label: "Total Downloads",
                  value: stats.totalDownloads,
                  icon: <FiDownload />,
                  gradient: "linear-gradient(135deg, #10b981, #059669)",
                },
                {
                  label: "Total Likes",
                  value: stats.totalLikes,
                  icon: <FiHeart />,
                  gradient: "linear-gradient(135deg, #ef4444, #dc2626)",
                },
                {
                  label: "Total Views",
                  value: stats.totalViews,
                  icon: <FiEye />,
                  gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                },
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  variants={statCardVariants}
                  whileHover="hover"
                  style={{
                    background: isDarkMode
                      ? "rgba(30, 30, 45, 0.8)"
                      : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "24px",
                    padding: "1.25rem",
                    textAlign: "center",
                    border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                  }}
                >
                  <div
                    style={{
                      background: stat.gradient,
                      width: "48px",
                      height: "48px",
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 0.75rem auto",
                    }}
                  >
                    <div style={{ color: "white", fontSize: "1.25rem" }}>
                      {stat.icon}
                    </div>
                  </div>
                  <h3
                    style={{
                      fontSize: "1.75rem",
                      fontWeight: "bold",
                      color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    }}
                  >
                    {stat.value}
                  </h3>
                  <p
                    style={{
                      color: isDarkMode ? "#94a3b8" : "#64748b",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            {/* Backlog Information Card */}
            <motion.div
              variants={itemVariants}
              style={{
                background: isDarkMode
                  ? "rgba(30, 30, 45, 0.8)"
                  : "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                padding: "1.5rem",
                border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    padding: "0.5rem",
                    borderRadius: "12px",
                  }}
                >
                  <FaBook color="white" size={16} />
                </div>
                <h3
                  style={{
                    color: isDarkMode ? "#f1f5f9" : "#1e293b",
                    fontWeight: 600,
                  }}
                >
                  Backlog Status
                </h3>
              </div>
              <motion.div
                whileHover={{ scale: hasBacklogs ? 1.01 : 1 }}
                onClick={() => hasBacklogs && setShowBacklogDetails(true)}
                style={{
                  background: hasBacklogs
                    ? "linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.05))"
                    : "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(16,185,129,0.05))",
                  padding: "1.25rem",
                  borderRadius: "20px",
                  cursor: hasBacklogs ? "pointer" : "default",
                  border: `1px solid ${hasBacklogs ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  {hasBacklogs ? (
                    <FaFire color="#ef4444" size={24} />
                  ) : (
                    <FaAward color="#10b981" size={24} />
                  )}
                  <div>
                    <p
                      style={{
                        color: hasBacklogs ? "#ef4444" : "#10b981",
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      {hasBacklogs
                        ? `${backlogCount} Backlog${backlogCount !== 1 ? "s" : ""} Remaining`
                        : "No Backlogs - Outstanding Performance"}
                    </p>
                    {hasBacklogs && (
                      <p
                        style={{
                          color: isDarkMode ? "#94a3b8" : "#64748b",
                          fontSize: "0.75rem",
                          marginTop: "0.5rem",
                        }}
                      >
                        Click to view backlog subjects and details
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}

        {activeTab === "notes" && (
          <motion.div
            key="notes"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4 }}
          >
            {/* View Toggle */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "1.5rem",
                gap: "0.5rem",
              }}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode("grid")}
                style={{
                  padding: "0.6rem",
                  background:
                    viewMode === "grid"
                      ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                      : isDarkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  color:
                    viewMode === "grid"
                      ? "white"
                      : isDarkMode
                        ? "#94a3b8"
                        : "#64748b",
                }}
              >
                <FiGrid size={18} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode("list")}
                style={{
                  padding: "0.6rem",
                  background:
                    viewMode === "list"
                      ? "linear-gradient(135deg, #8b5cf6, #3b82f6)"
                      : isDarkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.05)",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  color:
                    viewMode === "list"
                      ? "white"
                      : isDarkMode
                        ? "#94a3b8"
                        : "#64748b",
                }}
              >
                <FiList size={18} />
              </motion.button>
            </div>

            {recentNotes.length > 0 ? (
              viewMode === "grid" ? (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(320px, 1fr))",
                    gap: "1.25rem",
                  }}
                >
                  {recentNotes.map((note, idx) => {
                    const status = getStatusBadge(note.status);
                    return (
                      <motion.div
                        key={note._id}
                        variants={itemVariants}
                        whileHover={{ y: -5 }}
                      >
                        <Link
                          to={`/notes/${note._id}`}
                          style={{
                            textDecoration: "none",
                            background: isDarkMode
                              ? "rgba(30, 30, 45, 0.8)"
                              : "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(20px)",
                            padding: "1.25rem",
                            borderRadius: "20px",
                            border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                            display: "block",
                            transition: "all 0.3s",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "100px",
                              background: `linear-gradient(135deg, ${status.style.background.split(",")[0]}, transparent)`,
                              borderRadius: "16px",
                              marginBottom: "1rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FaFileAlt
                              size={40}
                              color={status.style.color || "white"}
                            />
                          </div>
                          <h3
                            style={{
                              color: isDarkMode ? "#f1f5f9" : "#1e293b",
                              marginBottom: "0.3rem",
                              fontWeight: 600,
                              fontSize: "1rem",
                            }}
                          >
                            {note.title}
                          </h3>
                          <p
                            style={{
                              color: isDarkMode ? "#94a3b8" : "#64748b",
                              fontSize: "0.7rem",
                              marginBottom: "0.75rem",
                            }}
                          >
                            {note.subjectCode}
                          </p>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginTop: "0.5rem",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                fontSize: "0.7rem",
                              }}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <FiEye size={12} /> {note.views || 0}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <FiDownload size={12} /> {note.downloads || 0}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <FiHeart size={12} /> {note.likes?.length || 0}
                              </span>
                            </div>
                            <div
                              style={{
                                ...status.style,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.3rem",
                                padding: "0.25rem 0.75rem",
                                borderRadius: "40px",
                                fontSize: "0.6rem",
                                fontWeight: 600,
                              }}
                            >
                              {status.icon} {status.text}
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </motion.div>
              ) : (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  {recentNotes.map((note, idx) => {
                    const status = getStatusBadge(note.status);
                    return (
                      <motion.div
                        key={note._id}
                        variants={itemVariants}
                        whileHover={{ x: 5 }}
                      >
                        <Link
                          to={`/notes/${note._id}`}
                          style={{
                            textDecoration: "none",
                            background: isDarkMode
                              ? "rgba(30, 30, 45, 0.8)"
                              : "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(20px)",
                            padding: isMobile ? "1rem" : "1.25rem",
                            borderRadius: "16px",
                            border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: "space-between",
                            alignItems: isMobile ? "flex-start" : "center",
                            flexWrap: "wrap",
                            gap: "0.8rem",
                          }}
                        >
                          <div>
                            <h3
                              style={{
                                color: isDarkMode ? "#f1f5f9" : "#1e293b",
                                marginBottom: "0.3rem",
                                fontWeight: 600,
                                fontSize: isMobile ? "0.9rem" : "1rem",
                              }}
                            >
                              {note.title}
                            </h3>
                            <p
                              style={{
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                fontSize: isMobile ? "0.7rem" : "0.8rem",
                              }}
                            >
                              {note.subjectCode}
                            </p>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "1rem",
                                color: isDarkMode ? "#94a3b8" : "#64748b",
                                fontSize: isMobile ? "0.7rem" : "0.8rem",
                              }}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <FiEye size={isMobile ? 12 : 14} />{" "}
                                {note.views || 0}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <FiDownload size={isMobile ? 12 : 14} />{" "}
                                {note.downloads || 0}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <FiHeart size={isMobile ? 12 : 14} />{" "}
                                {note.likes?.length || 0}
                              </span>
                            </div>
                            <div
                              style={{
                                ...status.style,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.3rem",
                                padding: "0.25rem 0.75rem",
                                borderRadius: "40px",
                                fontSize: isMobile ? "0.6rem" : "0.7rem",
                                fontWeight: 600,
                              }}
                            >
                              {status.icon} {status.text}
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  textAlign: "center",
                  padding: "4rem",
                  background: isDarkMode
                    ? "rgba(30, 30, 45, 0.8)"
                    : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
                }}
              >
                <div style={{ fontSize: 48, marginBottom: "1rem" }}>
                  <FiFolder
                    size={48}
                    color={isDarkMode ? "#475569" : "#cbd5e1"}
                  />
                </div>
                <p
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    marginTop: "1rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  You haven't uploaded any notes yet.
                </p>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/upload"
                    style={{
                      padding: "0.875rem 2rem",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "16px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      fontWeight: 600,
                      boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                    }}
                  >
                    <FiUploadCloud /> Upload your first note
                  </Link>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backlog Details Modal */}
      <AnimatePresence>
        {showBacklogDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.7)",
              backdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "1rem",
            }}
            onClick={() => setShowBacklogDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25 }}
              style={{
                background: isDarkMode
                  ? "linear-gradient(135deg, #1e1e2d, #1a1a2e)"
                  : "white",
                borderRadius: "28px",
                maxWidth: "650px",
                width: "100%",
                maxHeight: "85vh",
                overflow: "auto",
                padding: isMobile ? "1.25rem" : "2rem",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                border: `1px solid ${isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)"}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      background: "linear-gradient(135deg, #ef4444, #dc2626)",
                      padding: "0.5rem",
                      borderRadius: "14px",
                    }}
                  >
                    <FaFire color="white" size={18} />
                  </div>
                  <h2
                    style={{
                      color: "#ef4444",
                      fontSize: isMobile ? "1.25rem" : "1.5rem",
                      fontWeight: 700,
                    }}
                  >
                    Backlog Subjects
                  </h2>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowBacklogDetails(false)}
                  style={{
                    background: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ✕
                </motion.button>
              </div>

              {backlogSubjects.length === 0 ? (
                <p
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    textAlign: "center",
                    padding: "2rem",
                  }}
                >
                  No backlogs found
                </p>
              ) : (
                backlogSubjects.map((sem, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    style={{ marginBottom: "1.5rem" }}
                  >
                    <h3
                      style={{
                        background: "linear-gradient(135deg, #ef4444, #dc2626)",
                        color: "white",
                        padding: "10px 16px",
                        borderRadius: "14px",
                        marginBottom: "12px",
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        fontWeight: 600,
                      }}
                    >
                      Semester {sem.semester} Backlogs
                    </h3>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          minWidth: "300px",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              background: isDarkMode
                                ? "rgba(255,255,255,0.05)"
                                : "rgba(0,0,0,0.02)",
                              borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
                            }}
                          >
                            <th
                              style={{
                                padding: "10px",
                                textAlign: "left",
                                color: isDarkMode ? "#cbd5e1" : "#475569",
                              }}
                            >
                              Subject Code
                            </th>
                            <th
                              style={{
                                padding: "10px",
                                textAlign: "left",
                                color: isDarkMode ? "#cbd5e1" : "#475569",
                              }}
                            >
                              Subject Name
                            </th>
                            <th
                              style={{
                                padding: "10px",
                                textAlign: "center",
                                color: isDarkMode ? "#cbd5e1" : "#475569",
                              }}
                            >
                              Marks
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sem.subjects.map((sub, subIdx) => (
                            <tr
                              key={subIdx}
                              style={{
                                borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"}`,
                              }}
                            >
                              <td
                                style={{
                                  padding: "10px",
                                  fontFamily: "monospace",
                                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                                  color: isDarkMode ? "#94a3b8" : "#64748b",
                                }}
                              >
                                {sub.code}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                                  color: isDarkMode ? "#94a3b8" : "#64748b",
                                }}
                              >
                                {sub.name}
                              </td>
                              <td
                                style={{
                                  padding: "10px",
                                  textAlign: "center",
                                  color: "#ef4444",
                                  fontWeight: "bold",
                                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                                }}
                              >
                                {sub.marks}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                ))
              )}

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowBacklogDetails(false)}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "16px",
                  cursor: "pointer",
                  marginTop: "20px",
                  fontSize: isMobile ? "0.85rem" : "0.9rem",
                  fontWeight: 600,
                }}
              >
                Close
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            50% { transform: translate(20px, 20px) rotate(10deg); }
          }
          * {
            scrollbar-width: thin;
            scrollbar-color: #8b5cf6 #1e1e2d;
          }
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          ::-webkit-scrollbar-track {
            background: #1e1e2d;
            border-radius: 10px;
          }
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #8b5cf6, #3b82f6);
            border-radius: 10px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #7c3aed, #2563eb);
          }
        `}
      </style>
    </motion.div>
  );
};

export default Dashboard;
